"use client";

import { useEffect, useState, createContext, useContext } from "react";
import { db } from "@/lib/db";
import type { AiProfile } from "@/types/ai";
import BrandStyles from "@/components/branding/BrandStyles";

interface ThemeContextType {
  profile: AiProfile | null;
  loading: boolean;
  refreshProfile: () => Promise<void>;
}

const ThemeContext = createContext<ThemeContextType>({
  profile: null,
  loading: true,
  refreshProfile: async () => {},
});

export function useTheme() {
  return useContext(ThemeContext);
}

export default function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [profile, setProfile] = useState<AiProfile | null>(null);
  const [loading, setLoading] = useState(true);

  async function loadProfile() {
    try {
      if (typeof window === "undefined" || !db || !db.aiProfiles) {
        setLoading(false);
        return;
      }

      // Load the most recent profile
      const profiles = await db.aiProfiles.orderBy("createdAt").reverse().limit(1).toArray();
      if (profiles.length > 0) {
        setProfile(profiles[0]);
      }
    } catch (error) {
      console.error("Failed to load profile for theme:", error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadProfile();

    // Listen for profile updates
    const handleStorageChange = () => {
      loadProfile();
    };
    window.addEventListener('focus', handleStorageChange);
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('focus', handleStorageChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (
    <ThemeContext.Provider value={{ profile, loading, refreshProfile: loadProfile }}>
      {profile && <BrandStyles brand={profile.brand} />}
      {children}
    </ThemeContext.Provider>
  );
}

